import React, { FC, useRef, useState, useCallback } from 'react';
import { Button, Flex, Card } from '@gravity-ui/uikit';
import { toastError } from '../../libs/toasts/toasts';
import { formsT, toastsT, commonT } from '../../i18n';
import {
    THUMBNAIL_INPUT_ACCEPT_ATTRIBUTE,
    MAX_THUMBNAIL_SIZE,
} from '../../constants/thumbnailUploadAcceptTypes';
import './styles/ThumbnailUpload.css';

/**
 * Компонент для выбора превью видео.
 * Отображает UI для выбора файла, но не загружает его самостоятельно.
 * Фактическое сохранение происходит через onChange callback.
 *
 * Backend API:
 * - Форматы и ограничения: https://a.yandex-team.ru/arcadia/videoplatform/cloud/tusd/internal/service/fileservice/thumbnails.go
 * - Создание превью: https://a.yandex-team.ru/arcadia/videoplatform/cloud/vzhik/internal/handler/publicapi/thumbnail.go?rev=r18783924#L92
 * - Обновление видео: https://a.yandex-team.ru/arcadia/videoplatform/cloud/vzhik/internal/handler/publicapi/video.go?rev=r18783924#L228
 */

export type ThumbnailAction = 'none' | 'upload' | 'delete';

export type ThumbnailChange = {
    action: ThumbnailAction;
    file?: File;
};

type ThumbnailUploadProps = {
    currentThumbnailId?: string;
    onChange?: (change: ThumbnailChange) => void;
    disabled?: boolean;
    compact?: boolean;
    hideLabel?: boolean;
};

export const ThumbnailUpload: FC<ThumbnailUploadProps> = ({
    currentThumbnailId,
    onChange,
    disabled = false,
    compact = false,
    hideLabel = false,
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (file) {
                if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
                    toastError.open(toastsT('thumbnailInvalidFormat'));
                    return;
                }

                if (file.size > MAX_THUMBNAIL_SIZE) {
                    toastError.open(toastsT('thumbnailTooLarge'));
                    return;
                }

                setSelectedFile(file);
                setPendingDelete(false);

                const reader = new FileReader();
                reader.onload = (e) => {
                    setPreviewUrl(e.target?.result as string);
                };
                reader.readAsDataURL(file);

                if (onChange) {
                    onChange({ action: 'upload', file });
                }
            }
        },
        [onChange],
    );

    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleDelete = useCallback(() => {
        setPendingDelete(true);
        setSelectedFile(null);
        setPreviewUrl(null);

        if (onChange) {
            onChange({ action: 'delete' });
        }
    }, [onChange]);

    const handleCancel = useCallback(() => {
        setSelectedFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    return (
        <div className={`thumbnail-upload ${compact ? 'thumbnail-upload--compact' : ''}`}>
            <input
                ref={fileInputRef}
                type="file"
                accept={THUMBNAIL_INPUT_ACCEPT_ATTRIBUTE}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                disabled={disabled}
            />

            <Flex direction="column" gap={3}>
                {!hideLabel && (
                    <label className="thumbnail-upload__label">{formsT('thumbnail')}</label>
                )}

                {previewUrl && (
                    <Card className="thumbnail-upload__preview">
                        <img src={previewUrl} alt="Thumbnail preview" />
                    </Card>
                )}

                <div className="thumbnail-upload__hint">{formsT('thumbnailRecommendation')}</div>

                <Flex gap={2}>
                    {!selectedFile && !pendingDelete && !currentThumbnailId && (
                        <Button view="outlined" onClick={handleUploadClick} disabled={disabled}>
                            {formsT('uploadThumbnail')}
                        </Button>
                    )}

                    {!selectedFile && !pendingDelete && currentThumbnailId && (
                        <>
                            <Button view="outlined" onClick={handleUploadClick} disabled={disabled}>
                                {formsT('changeThumbnail')}
                            </Button>
                            <Button
                                view="outlined-danger"
                                onClick={handleDelete}
                                disabled={disabled}
                            >
                                {formsT('deleteThumbnail')}
                            </Button>
                        </>
                    )}

                    {selectedFile && (
                        <Button view="outlined" onClick={handleCancel} disabled={disabled}>
                            {commonT('cancel')}
                        </Button>
                    )}

                    {pendingDelete && (
                        <Button view="outlined" onClick={handleCancel} disabled={disabled}>
                            {commonT('cancel')}
                        </Button>
                    )}
                </Flex>
            </Flex>
        </div>
    );
};
